import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send, Instagram, Linkedin, Facebook, Youtube } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We will respond shortly.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Address',
      content: '28 Broadway Ave, Suite 209\nKissimmee, FL 34741',
      link: 'https://maps.google.com/?q=28+Broadway+Ave+Suite+209+Kissimmee+FL+34741'
    },
    {
      icon: Phone,
      title: 'Phone',
      content: '1-866-310-3380',
      link: 'tel:1-866-310-3380'
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'info@dominicanchamberfl.org',
      link: 'mailto:info@dominicanchamberfl.org'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      content: 'Monday - Friday\n9:00 AM - 5:00 PM EST',
      link: null
    }
  ];

  return (
    <div className="min-h-screen pt-16 sm:pt-20">
      {/* Hero Section */}
      <section className="relative py-14 sm:py-20 bg-[#002249]" data-testid="contact-hero">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-xs sm:text-sm font-bold text-[#E63946] tracking-wider uppercase mb-3 block">
              Contact Us
            </span>
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-5">
              Get In Touch
            </h1>
            <p className="text-white/75 text-sm sm:text-base lg:text-lg leading-relaxed">
              Have questions about membership, events, or partnerships? 
              We'd love to hear from you. Reach out and our team will respond promptly.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white" data-testid="contact-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-2xl font-bold text-[#002249] mb-6">Contact Information</h2>
                <div className="space-y-6">
                  {contactInfo.map((item, index) => (
                    <div key={index} className="flex items-start space-x-4" data-testid={`contact-info-${index}`}>
                      <div className="w-12 h-12 bg-[#002249]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-6 h-6 text-[#002249]" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#002249] mb-1">{item.title}</h4>
                        {item.link ? (
                          <a 
                            href={item.link} 
                            target={item.link.startsWith('http') ? '_blank' : '_self'}
                            rel="noopener noreferrer"
                            className="text-slate-600 hover:text-[#E63946] transition-colors whitespace-pre-line"
                          >
                            {item.content}
                          </a>
                        ) : (
                          <p className="text-slate-600 whitespace-pre-line">{item.content}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Social Links */}
                <div className="mt-8 pt-8 border-t border-slate-200">
                  <h4 className="font-semibold text-[#002249] mb-4">Follow Us</h4>
                  <div className="flex space-x-3">
                    {[
                      { icon: Instagram, label: 'Instagram', href: 'https://www.instagram.com/dominicanchamberfl/' },
                      { icon: Linkedin, label: 'LinkedIn', href: 'https://www.linkedin.com/in/dominican-chamber-of-commerce-of-florida-dccf-b4a736376' },
                      { icon: Facebook, label: 'Facebook', href: 'https://www.facebook.com/p/Dominican-Chamber-of-Florida-61576763651371/' },
                      { icon: Youtube, label: 'YouTube', href: 'https://www.youtube.com/@DominicanChamberOfFlorida' },
                    ].map((s) => (
                      <a
                        key={s.label}
                        href={s.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 bg-[#002249] rounded-sm flex items-center justify-center text-white hover:bg-[#E63946] hover:scale-110 transition-all duration-200"
                        aria-label={s.label}
                        data-testid={`social-${s.label.toLowerCase()}`}
                      >
                        <s.icon className="w-4 h-4" />
                      </a>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-slate-50 p-8 rounded-xl border border-slate-200"
              >
                <h2 className="text-2xl font-bold text-[#002249] mb-6">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="h-12 rounded-lg"
                        data-testid="contact-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="h-12 rounded-lg"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="h-12 rounded-lg"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject">Subject *</Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="h-12 rounded-lg"
                        data-testid="contact-subject"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      placeholder="How can we help you?"
                      className="rounded-sm"
                      data-testid="contact-message"
                    />
                  </div>

                  <Button 
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full md:w-auto bg-[#002249] hover:bg-[#0a3570] rounded-sm px-8 h-12"
                    data-testid="contact-submit"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <Send className="ml-2 w-5 h-5" />
                  </Button>
                </form>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="h-96 bg-slate-200" data-testid="contact-map">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3518.5372261776163!2d-81.4185!3d28.2920!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDE3JzMxLjIiTiA4McKwMjUnMDYuNiJX!5e0!3m2!1sen!2sus!4v1620000000000!5m2!1sen!2sus"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="DCCF Location"
        />
      </section>
    </div>
  );
};

export default ContactPage;
